'use client';

import { amplitudeSendScreenEvent, getScreenName } from '@musinsa/frontend-utils';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { SNOWPLOW_SCHEME } from '../../snowplow';

interface PageViewTrackerProps {
  pageName?: string;
  // TODO: Snowplow(pageName)와 Amplitude(musinsaPageName) 네이밍 통합 방식 논의 필요
  musinsaPageName?: string;
  /**
   * 양쪽 서비스(29CM Snowplow, 무신사 Amplitude)에 공통으로 전달되는 프로퍼티.
   * 29CM view_page 스키마는 additionalProperties: false 이므로
   * 스키마에 정의되지 않은 필드는 musinsaProperties로 분리해야 함.
   */
  properties?: Record<string, unknown>;
  /**
   * 무신사 Amplitude 전용 프로퍼티. properties와 병합되어 amplitudeSendScreenEvent에 전달됨.
   * 29CM Snowplow에는 전달되지 않음.
   */
  musinsaProperties?: Record<string, unknown>;
  /**
   * 29CM Snowplow 전용 프로퍼티. properties와 병합되어 Snowplow view_page에 전달됨.
   * 무신사 Amplitude에는 전달되지 않음.
   */
  snowplowProperties?: Record<string, unknown>;
}

/**
 * pathname을 Snowplow page_name으로 변환
 *
 * @example
 * / -> curator_home
 * /intro -> curator_intro
 * /shop/abc -> curator_shop_abc
 * /profile/edit/nickname -> curator_profile_edit_nickname
 */
const generatePageNameFromPathname = (pathname: string): string => {
  if (pathname === '/') {
    return 'curator_home';
  }

  const pageName = pathname.split('/').filter(Boolean).join('_');

  return `curator_${pageName}`;
};

export function PageViewTracker({
  pageName,
  musinsaPageName,
  properties,
  musinsaProperties,
  snowplowProperties,
}: PageViewTrackerProps) {
  const pathname = usePathname();
  const hasTracked = useRef(false);
  const prevPathname = useRef(pathname);
  const { service } = useDeviceInfo();

  // NOTE: properties를 JSON 문자열로 변환하여 값 비교 가능하게 함(1회 더 찍히는 것을 방지)
  const propertiesString = properties ? JSON.stringify(properties) : '';
  const musinsaPropertiesString = musinsaProperties ? JSON.stringify(musinsaProperties) : '';
  const snowplowPropertiesString = snowplowProperties ? JSON.stringify(snowplowProperties) : '';

  useEffect(() => {
    // NOTE: pathname이 변경되었으면 hasTracked를 리셋
    if (prevPathname.current !== pathname) {
      hasTracked.current = false;
      prevPathname.current = pathname;
    }

    // NOTE: service가 아직 확정되지 않았으면 대기
    if (!service) return;

    // NOTE: 이미 트래킹했다면 무시 (방문당 1회 보장)
    if (hasTracked.current) {
      return;
    }

    const trackPageView = () => {
      try {
        if (service === SERVICE['29CM']) {
          const finalPageName = pageName || generatePageNameFromPathname(pathname);
          track({
            schema: SNOWPLOW_SCHEME.VIEW_PAGE,
            data: { page_name: finalPageName, ...properties, ...snowplowProperties },
          });
        }

        // NOTE: musinsaPageName=""이면 Musinsa 발화를 skip (빈 문자열은 명시적 skip 의도)
        if (service === SERVICE.MUSINSA && musinsaPageName !== '') {
          const eventName = musinsaPageName || pageName || generatePageNameFromPathname(pathname);
          amplitudeSendScreenEvent({
            event_name: eventName,
            from: getScreenName(),
            ...properties,
            // NOTE: musinsaProperties는 무신사 전용 필드로 properties 위에 병합
            ...musinsaProperties,
          });
        }

        hasTracked.current = true;
      } catch (error) {
        console.error('[PageView] Failed to track page view:', error);
      }
    };

    // NOTE: 화면 렌더링 완료 후 이벤트 발생을 보장
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(trackPageView);
    } else {
      requestAnimationFrame(() => {
        setTimeout(trackPageView, 0);
      });
    }
  }, [
    pathname,
    pageName,
    musinsaPageName,
    propertiesString,
    musinsaPropertiesString,
    snowplowPropertiesString,
    service,
  ]);

  return null;
}
